/** Estado só de tela (Zustand): avisos com "Desfazer" e tema. */
import { create } from 'zustand';

export interface Toast {
  id: number;
  text: string;
  tone?: 'default' | 'error';
  action?: { label: string; run: () => void };
  /** ação destrutiva adiada: roda quando o aviso some, se não desfizer */
  onExpire?: () => void;
  duration: number;
}

interface UiState {
  toasts: Toast[];
  toast: (t: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => number;
  dismiss: (id: number, opts?: { expire?: boolean }) => void;
  theme: 'auto' | 'light' | 'dark';
  setTheme: (t: 'auto' | 'light' | 'dark') => void;
}

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function readTheme(): 'auto' | 'light' | 'dark' {
  try {
    const v = localStorage.getItem('lindeza:theme');
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

export function applyTheme(theme: 'auto' | 'light' | 'dark'): void {
  const root = document.documentElement;
  if (theme === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export const useUi = create<UiState>((set, get) => ({
  toasts: [],
  toast: (t) => {
    const id = nextId++;
    const toast: Toast = { duration: t.action ? 6000 : 3500, ...t, id };
    set((s) => ({ toasts: [...s.toasts.slice(-2), toast] }));
    timers.set(
      id,
      setTimeout(() => get().dismiss(id, { expire: true }), toast.duration),
    );
    return id;
  },
  dismiss: (id, opts = {}) => {
    const t = get().toasts.find((x) => x.id === id);
    const timer = timers.get(id);
    if (timer) clearTimeout(timer);
    timers.delete(id);
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
    if (t && opts.expire) t.onExpire?.();
  },
  theme: readTheme(),
  setTheme: (theme) => {
    try {
      localStorage.setItem('lindeza:theme', theme);
    } catch {
      // modo privado
    }
    applyTheme(theme);
    set({ theme });
  },
}));

/** Atalhos. */
export const toast = (text: string, extra: Partial<Omit<Toast, 'id' | 'text'>> = {}) => useUi.getState().toast({ text, ...extra });
export const toastError = (e: unknown) =>
  useUi.getState().toast({ text: e instanceof Error ? e.message : 'Algo deu errado.', tone: 'error' });

/** Ação desfazível: roda já e oferece "Desfazer" (plano, 14.5). */
export function withUndo(text: string, undo: () => unknown): void {
  useUi.getState().toast({ text, action: { label: 'Desfazer', run: () => void undo() } });
}

/** Ação destrutiva adiada: só acontece se ninguém tocar em "Desfazer". */
export function deferDestructive(text: string, commit: () => unknown): void {
  let undone = false;
  useUi.getState().toast({
    text,
    action: {
      label: 'Desfazer',
      run: () => {
        undone = true;
      },
    },
    onExpire: () => {
      if (!undone) void commit();
    },
  });
}
